import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Box} from '@mui/material';
import Forms from '../../../components/Forms';
import Error from '../../../components/Error';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';
import moment from 'moment';
import ReactQuill from "react-quill";

const urlRegex = /^https?:\/\/.+/i;

const GeneralSettings = () => {
    const {t} = useTranslation();
    const firstInputRef = useRef(null);

    const [_id, setId] = useState(null);

    const [facebookUrl, setFacebookUrl] = useState('');
    const [facebookName, setFacebookName] = useState('');

    const [instagramUrl, setInstagramUrl] = useState('');
    const [instagramName, setInstagramName] = useState('');

    const [merchUrl, setMerchUrl] = useState('');

    const [curriculumVitae, setCurriculumVitae] = useState('');
    const [curriculumVitaePreview, setCurriculumVitaePreview] = useState('');

    const [smtpHost, setSmtpHost] = useState('');
    const [smtpUser, setSmtpUser] = useState('');
    const [smtpPassword, setSmtpPassword] = useState('');
    const [smtpSecurity, setSmtpSecurity] = useState(null);

    const [contactInformation, setContactInformation] = useState('');
    const quillRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [updatedAt, setUpdatedAt] = useState(null);

    const toolbarModules = useMemo(() => ({
        toolbar: {
            container: [
                [{'header': [1, 2, 3, false]}],
                ['bold', 'italic', 'underline', 'strike'],
                [{'list': 'ordered'}, {'list': 'bullet'}],
                ['link', 'image'],
                ['clean']
            ]
        }
    }), []);

    useEffect(() => {
        setTimeout(() => firstInputRef.current?.focus(), 1);
    }, []);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                setLoading(true);
                const res = await API.get(API_ENDPOINTS.generalSettings.get);
                const s = res.data || {};
                setId(s._id || null);
                setCurriculumVitae(s.curriculumVitae || '');
                setCurriculumVitaePreview(s.curriculumVitaePreview || '');
                setContactInformation(s.contactInformation || '');
                setFacebookUrl(s.facebookUrl || '');
                setFacebookName(s.facebookName || '');
                setInstagramUrl(s.instagramUrl || '');
                setInstagramName(s.instagramName || '');
                setMerchUrl(s.merchUrl || '');
                setSmtpHost(s.smtpSettings?.host || '');
                setSmtpUser(s.smtpSettings?.user || '');
                setSmtpPassword(s.smtpSettings?.password || '');
                setSmtpSecurity(s.smtpSettings?.security || '');
                setUpdatedAt(s?.updatedAt || null);
            } catch (e) {
                if (e?.response?.status !== 404) setError(e);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const errors = useMemo(() => {
        const e = {};
        if (!smtpHost.trim()) e.smtpHost = t('validation.required');
        if (!smtpUser.trim()) e.smtpUser = t('validation.required');
        if (!_id && !smtpPassword) e.smtpPassword = t('validation.required');
        if (facebookUrl && !urlRegex.test(facebookUrl)) e.facebookUrl = t('validation.invalidUrl');
        if (instagramUrl && !urlRegex.test(instagramUrl)) e.instagramUrl = t('validation.invalidUrl');
        return e;
    }, [smtpHost, smtpUser, smtpPassword, _id, facebookUrl, instagramUrl, t]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const payload = {
            contactInformation: contactInformation.trim(),
            curriculumVitae: curriculumVitae.trim(),
            curriculumVitaePreview: curriculumVitaePreview.trim(),
            facebookUrl: facebookUrl.trim() || undefined,
            facebookName: facebookName.trim() || undefined,
            instagramUrl: instagramUrl.trim() || undefined,
            instagramName: instagramName.trim() || undefined,
            merchUrl: merchUrl.trim() || undefined,
            smtpSettings: {
                host: smtpHost.trim(),
                user: smtpUser.trim(),
                security: smtpSecurity.trim(),
                ...(smtpPassword ? {password: smtpPassword} : {})
            }
        };

        try {
            setSaving(true);
            await API.post(API_ENDPOINTS.generalSettings.edit, payload);
            const res = await API.get(API_ENDPOINTS.generalSettings.edit);
            const s = res.data || {};
            setId(s._id || null);
            setUpdatedAt(s.updatedAt || s.updated_at || new Date().toISOString());
            setSmtpPassword('');
        } catch (e2) {
            setError(e2);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div>{t('loading')}</div>;
    }

    return (
        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4 max-w-4xl">
            <div className="col-span-2">
                <h2 className="text-xl font-semibold mb-2">{t('generalSettings.general')}</h2>
                {updatedAt && (
                    <div className="text-sm text-gray-500 mb-2">
                        {t('updatedAt')}: {moment(updatedAt).format('DD.MM.YYYY HH:mm')}
                    </div>
                )}
            </div>

            {/* kontaktní údaje */}
            <Box className="col-span-2 p-4 rounded-md border border-gray-200">
                <div className="col-span-2 mt-2">
                    <h3 className="text-lg font-medium mb-2">{t('generalSettings.contactDetails')}</h3>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-2">
                    <Forms.Text
                        label={t('generalSettings.facebookUrl')}
                        placeholder={t('generalSettings.facebookUrl')}
                        value={facebookUrl}
                        onChange={setFacebookUrl}
                        error={errors.facebookUrl}
                        className="rounded-md"
                    />

                    <Forms.Text
                        label={t('generalSettings.facebookName')}
                        placeholder={t('generalSettings.facebookName')}
                        value={facebookName}
                        onChange={setFacebookName}
                        error={errors.facebookName}
                        className="rounded-md"
                    />
                </div>

                <div className="grid grid-cols-2 gap-4 mt-2">
                    <Forms.Text
                        label={t('generalSettings.instagramUrl')}
                        placeholder={t('generalSettings.instagramUrl')}
                        value={instagramUrl}
                        onChange={setInstagramUrl}
                        error={errors.instagramUrl}
                        className="rounded-md"
                    />

                    <Forms.Text
                        label={t('generalSettings.merchUrl')}
                        placeholder={t('generalSettings.merchUrl')}
                        value={merchUrl}
                        onChange={setMerchUrl}
                        error={errors.merchUrl}
                        className="rounded-md"
                    />
                </div>


                <div className="col-span-2 mt-2">
                    <label>{t('generalSettings.contactInformation')}</label>
                    <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={contactInformation}
                        onChange={setContactInformation}
                        modules={toolbarModules}
                    />
                </div>
            </Box>

            <Box className="col-span-2 p-4  rounded-md border border-gray-200">

                <div className="col-span-2 mt-2">
                    <h3 className="text-lg font-medium mb-2">{t('generalSettings.curriculumVitae')}</h3>
                </div>

                <div className="col-span-2 mt-2">
                    <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={curriculumVitae}
                        onChange={setCurriculumVitae}
                        modules={toolbarModules}
                    />
                </div>
            </Box>

            <Box className="col-span-2 p-4  rounded-md border border-gray-200">

                <div className="col-span-2 mt-2">
                    <h3 className="text-lg font-medium mb-2">{t('generalSettings.curriculumVitaePreview')}</h3>
                </div>

                <div className="col-span-2 mt-2">
                    <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={curriculumVitaePreview}
                        onChange={setCurriculumVitaePreview}
                        modules={toolbarModules}
                    />
                </div>
            </Box>
            
            <Box className="col-span-2 p-4  rounded-md border border-gray-200">
                <div className="col-span-2 mt-2">
                    <h3 className="text-lg font-medium mb-2">{t('generalSettings.smtpSettings')}</h3>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-2">
                    <Forms.Text
                        required
                        label={t('generalSettings.host')}
                        placeholder={t('generalSettings.host')}
                        value={smtpHost}
                        onChange={setSmtpHost}
                        error={errors.smtpHost}
                        className="rounded-md"
                    />

                    <Forms.Text
                        required
                        label={t('generalSettings.user')}
                        placeholder={t('generalSettings.user')}
                        value={smtpUser}
                        onChange={setSmtpUser}
                        error={errors.smtpUser}
                        className="rounded-md"
                    />
                </div>
                <div className="grid grid-cols-2 gap-4 mt-2">
                    <Forms.Password
                        label={t('generalSettings.password')}
                        placeholder={t('generalSettings.password')}
                        value={smtpPassword}
                        onChange={setSmtpPassword}
                        error={errors.smtpPassword}
                        className="rounded-md"
                    />

                    <Forms.Select
                        label={t('generalSettings.security')}
                        value={{value: smtpSecurity, label: smtpSecurity}}
                        options={[
                            {value: null, label: 'Žádné'},
                            {value: 'ssl', label: 'ssl'},
                            {value: 'tls', label: 'tls'}
                        ]}
                        onChange={(opt) => setSmtpSecurity(opt?.value || '')}
                        className="rounded-md"
                    />
                </div>
            </Box>

            {error && <div className="col-span-2"><Error error={error}/></div>}

            <div className="col-span-2 flex justify-end mt-2">
                <Forms.Button submit disabled={saving} label={saving ? t('saving') : t('save')}/>
            </div>
        </form>
    );
};

export default GeneralSettings;
